import type { FC } from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { LoadingUI } from "../../../components/Loading";
import type {
  MatchingJobResult,
  MatchingResultItem,
} from "../../../hooks/use-free-matching-api";
import { MatchingResultCard } from "../../emails/EmailDetailPage/components/MatchingResultCard";
import { MatchingFormUI } from "./components/MatchingForm";

export interface MatchingPageProps {
  jobResult: MatchingJobResult | undefined;
  isLoading: boolean;
  error: Error | undefined;
  onSearch: (candidateInfo: string) => void;
  onStatusUpdate: (resultId: string, status: string) => void;
}

/**
 * 手動マッチングページ Presentation
 * @param {MatchingPageProps} props - コンポーネントProps
 * @return {JSX.Element} MatchingPage コンポーネント
 */
export const MatchingPage: FC<MatchingPageProps> = ({
  jobResult,
  isLoading,
  error,
  onSearch,
  onStatusUpdate,
}) => {
  const [candidateInfo, setCandidateInfo] = useState("");

  const handlePropose = (result: MatchingResultItem) => {
    onStatusUpdate(result.id, "proposed");
  };

  const handleCompare = (result: MatchingResultItem) => {
    if (result.matchedEmailId) {
      window.open(`/emails/search/${result.matchedEmailId}`, "_blank");
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold">フリーマッチング</h1>
        <p className="text-sm text-base-content/60 mt-1">
          人材情報や案件情報をフリーテキストで入力すると、AIが内容を判別し、マッチするメールを検索します
        </p>
      </div>

      <div className="grid grid-cols-1 gap-8">
        <MatchingFormUI
          candidateInfo={candidateInfo}
          isLoading={isLoading}
          onSubmit={onSearch}
          onCandidateInfoChange={setCandidateInfo}
        />

        {isLoading && (
          <div className="flex flex-col items-center justify-center space-y-4 py-12">
            <LoadingUI type="Grid" color="#34495E" width={60} height={60} />
            <p className="text-base-content/80 text-sm">
              AI がマッチング分析中です...
            </p>
          </div>
        )}

        {error && !isLoading && (
          <div className="alert alert-error">
            <span>{error.message}</span>
          </div>
        )}

        {!isLoading && jobResult && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">
                マッチング結果 ({jobResult.resultsSaved}件)
              </h2>
              <span className="text-sm text-base-content/60">
                {jobResult.candidatesSearched}件の候補から
                {jobResult.candidatesScored}件をスコアリング
              </span>
            </div>

            {jobResult.results.length === 0 ? (
              <div className="text-center py-8 text-base-content/60">
                <p>マッチする案件が見つかりませんでした</p>
                <p className="text-xs mt-1">検索条件を変更してお試しください</p>
                <p className="text-xs mt-3">
                  <Link
                    to="/settings/matching/conditions"
                    className="text-primary underline hover:text-primary/80"
                  >
                    マッチング条件設定画面
                  </Link>
                  で、マッチング判定を調整できます。
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {jobResult.results.map(result => (
                  <MatchingResultCard
                    key={result.id}
                    result={result}
                    onPropose={handlePropose}
                    onCompare={handleCompare}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchingPage;
